import {
  BarChartOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Empty, Input } from 'antd';
import { useMemo, useState } from 'react';
import { Container } from '../../../../../core/components';
import { Skill } from '../../job/models/job.model';

interface CandidateSkillsProps {
  skills: Skill[];
}

export type SkillLevel = 'expert' | 'advanced' | 'intermediate' | 'beginner';

const skillLevels: Record<
  SkillLevel,
  { title: string; description: string; order: number }
> = {
  expert: {
    title: 'Expert',
    description: 'Can lead, mentor, and make high-impact decisions.',
    order: 0,
  },
  advanced: {
    title: 'Advanced',
    description: 'Strong independent delivery in production environments.',
    order: 1,
  },
  intermediate: {
    title: 'Intermediate',
    description: 'Practical experience with occasional guidance.',
    order: 2,
  },
  beginner: {
    title: 'Beginner',
    description: 'Foundational knowledge and growing experience.',
    order: 3,
  },
};

export function normalizeSkillLevel(level?: string): SkillLevel {
  const normalized = level?.trim().toLowerCase();
  if (normalized && normalized in skillLevels) {
    return normalized as SkillLevel;
  }
  return 'beginner';
}

export function groupCandidateSkills(skills: Skill[] = []) {
  return skills.reduce<Record<SkillLevel, Skill[]>>(
    (groups, skill) => {
      groups[normalizeSkillLevel(skill?.skillLevel)].push(skill);
      return groups;
    },
    {
      expert: [],
      advanced: [],
      intermediate: [],
      beginner: [],
    }
  );
}

const CandidateSkills: React.FC<CandidateSkillsProps> = ({ skills = [] }) => {
  const [skillSearchTerm, setSkillSearchTerm] = useState('');
  const filteredSkills = useMemo(() => {
    const query = skillSearchTerm.trim().toLowerCase();
    if (!query) return skills;
    return skills.filter((skill) =>
      skill?.skillName?.toLowerCase().includes(query)
    );
  }, [skillSearchTerm, skills]);
  const skillsByLevel = useMemo(
    () => groupCandidateSkills(filteredSkills),
    [filteredSkills]
  );
  const strongestSkills =
    skillsByLevel.expert.length + skillsByLevel.advanced.length;

  return (
    <Container>
      <section className="candidate-section candidate-skills">
        <header className="candidate-section__header">
          <div>
            <span className="candidate-section__eyebrow">Profile evidence</span>
            <h2>Skills</h2>
            <p>
              Scan the candidate’s capabilities by proficiency and find specific
              experience quickly.
            </p>
          </div>
          <Input
            allowClear
            aria-label="Search candidate skills"
            placeholder="Search skills"
            prefix={<SearchOutlined />}
            value={skillSearchTerm}
            onChange={(event) => setSkillSearchTerm(event.target.value)}
            className="candidate-section__search"
          />
        </header>

        <div className="candidate-skills__summary">
          <div>
            <span className="candidate-skills__summary-icon">
              <BarChartOutlined />
            </span>
            <p>
              <strong>{skills.length}</strong>
              <span>Total skills</span>
            </p>
          </div>
          <div>
            <span className="candidate-skills__summary-icon candidate-skills__summary-icon--strong">
              <ThunderboltOutlined />
            </span>
            <p>
              <strong>{strongestSkills}</strong>
              <span>Advanced or expert</span>
            </p>
          </div>
          <div className="candidate-skills__distribution">
            {(Object.keys(skillLevels) as SkillLevel[]).map((level) => {
              const percentage = skills.length
                ? Math.round(
                    (groupCandidateSkills(skills)[level].length /
                      skills.length) *
                      100
                  )
                : 0;
              return (
                <span
                  key={level}
                  className={`candidate-skills__distribution-bar candidate-skills__distribution-bar--${level}`}
                  style={{ width: `${percentage}%` }}
                  title={`${skillLevels[level].title}: ${percentage}%`}
                />
              );
            })}
          </div>
        </div>

        {filteredSkills.length ? (
          <div className="candidate-skills__grid">
            {(Object.keys(skillLevels) as SkillLevel[])
              .sort(
                (left, right) =>
                  skillLevels[left].order - skillLevels[right].order
              )
              .map((level) => {
                const levelSkills = skillsByLevel[level];
                if (!levelSkills.length) return null;
                return (
                  <article
                    key={level}
                    className={`candidate-skill-group candidate-skill-group--${level}`}
                  >
                    <div className="candidate-skill-group__heading">
                      <span />
                      <div>
                        <h3>{skillLevels[level].title}</h3>
                        <p>{skillLevels[level].description}</p>
                      </div>
                      <strong>{levelSkills.length}</strong>
                    </div>
                    <div className="candidate-skill-group__list">
                      {levelSkills.map((skill, index) => (
                        <span
                          key={`${skill?.skillName ?? ''}-${skill?.skillLevel ?? ''}-${index}`}
                        >
                          {skill?.skillName || 'Unnamed skill'}
                        </span>
                      ))}
                    </div>
                  </article>
                );
              })}
          </div>
        ) : (
          <div className="candidate-section__empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                skillSearchTerm
                  ? `No skills match “${skillSearchTerm}”`
                  : 'No skills have been added to this profile yet.'
              }
            />
          </div>
        )}
      </section>
    </Container>
  );
};

export default CandidateSkills;
